import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { clearRpcCache } from "@/lib/rpc-cache";

const STORAGE_KEY = "tgh_discord_join_clicked_at";

type SyncStatus = "idle" | "syncing" | "synced" | "error";

const DiscordJoinSyncContext = createContext<SyncStatus>("idle");

/**
 * Provider that triggers one immediate Discord-member sync when the user
 * returns to the site after clicking a Discord join link. It reads the click
 * timestamp from localStorage, so it works across page navigations and on any
 * page in the app.
 */
export function DiscordJoinSyncProvider({ children }: { children: ReactNode }) {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");

  useEffect(() => {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const clickedAt = raw ? Number(raw) : null;
    if (!clickedAt || Number.isNaN(clickedAt)) return;

    let cancelled = false;
    let running = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 4;
    const timers: number[] = [];

    const runSync = async () => {
      if (cancelled || running || document.hidden) return;
      const elapsed = Date.now() - clickedAt;
      if (elapsed > 10 * 60 * 1000) {
        window.localStorage.removeItem(STORAGE_KEY);
        return;
      }
      if (attempts >= MAX_ATTEMPTS) return;
      attempts += 1;
      running = true;
      setSyncStatus("syncing");
      try {
        const { data, error } = await supabase.functions.invoke("sync-recent-discord-joins", { body: {} });
        if (error) throw error;
        setSyncStatus("synced");
        // Only stop retrying once the sync actually picked up a member.
        const matched = (data as { synced?: number; matched?: number } | null)?.synced ??
          (data as { matched?: number } | null)?.matched ?? 0;
        if (matched > 0) {
          // Fresh member — refresh the cached public counts/previews.
          clearRpcCache();
          attempts = MAX_ATTEMPTS;
          window.localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.error("Recent Discord sync failed", e);
        setSyncStatus("error");
      } finally {
        running = false;
      }
    };

    // Staggered attempts: the user usually finishes the Discord join a few
    // seconds to a minute after clicking the invite link.
    [3000, 20000, 60000, 150000].forEach((delay) => {
      timers.push(window.setTimeout(runSync, delay));
    });

    const onVisible = () => {
      if (!document.hidden) void runSync();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  return <DiscordJoinSyncContext.Provider value={syncStatus}>{children}</DiscordJoinSyncContext.Provider>;
}

/** Read the current Discord join sync status from anywhere in the layout tree. */
export function useDiscordJoinSync() {
  return useContext(DiscordJoinSyncContext);
}
